/**
 * useSubSubtaskMutations — Mutation hooks for sub-subtask CRUD operations.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "./useAuth";

export const useSubSubtaskMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const addSubSubtaskMutation = useMutation({
    mutationFn: async ({ subtaskId, subSubtaskText }: { subtaskId: string, subSubtaskText: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data, error } = await supabase
        .from('sub_subtasks')
        .insert([{
          subtask_id: subtaskId,
          text: subSubtaskText,
          completed: false
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      toast({
        title: "Sub-subtask added",
        description: "New sub-subtask has been added successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding sub-subtask",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const toggleSubSubtaskMutation = useMutation({
    mutationFn: async ({ subSubtaskId }: { subSubtaskId: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data: subSubtask } = await supabase
        .from('sub_subtasks')
        .select('completed')
        .eq('id', subSubtaskId)
        .single();

      const { data, error } = await supabase
        .from('sub_subtasks')
        .update({ completed: !subSubtask?.completed })
        .eq('id', subSubtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error toggling sub-subtask",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const deleteSubSubtaskMutation = useMutation({
    mutationFn: async ({ subSubtaskId }: { subSubtaskId: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { error } = await supabase
        .from('sub_subtasks')
        .delete()
        .eq('id', subSubtaskId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      toast({
        title: "Sub-subtask deleted",
        description: "Sub-subtask has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting sub-subtask",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  return {
    addSubSubtask: addSubSubtaskMutation.mutate,
    toggleSubSubtask: toggleSubSubtaskMutation.mutate,
    deleteSubSubtask: deleteSubSubtaskMutation.mutate,
  };
};
